import { useState, useEffect, useRef } from 'react';
import {
  Mic, MicOff, Save, Trash2,
  Clock, Calendar, User, FileText, Edit3,
  Sparkles, CheckCircle
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useAIStore } from '../stores/useAIStore';
import { useVoiceStore } from '../stores/useVoiceStore';
import { usePatientStore } from '../stores/usePatientStore';
// import { usePatients } from '../hooks/usePatients'; // Disabled - using mock data from App.tsx instead
import { CircularVisualizer } from './AudioVisualizer';
import { getLocalizedValue } from '../utils/localization';
import { Button } from '@/components/ui/button';
import { BrandSelect } from '@/components/ui/BrandSelect';
import { Textarea } from '@/components/ui/textarea';

interface AIVisitRecordingProps {
  className?: string;
  onClose?: () => void;
}

export const AIVisitRecording: React.FC<AIVisitRecordingProps> = ({
  className = '',
  onClose: _onClose
}) => {
  const { language } = useTranslation();
  const { formatDate, formatDateTime } = useDateFormat();
  const {
    visitRecordings,
    activeRecording,
    isRecordingVisit,
    startVisitRecording,
    stopVisitRecording,
    updateActiveRecording,
    updateVisitRecordingTranscription,
    loadVisitRecording,
    deleteVisitRecording,
    addContext
  } = useAIStore();

  const {
    startDictation,
    stopDictation,
    isRecording,
    audioLevel,
    currentDictation
  } = useVoiceStore();

  const { patients } = usePatientStore();
  // const { data: patientsData, isLoading } = usePatients(); // Disabled - using mock data from App.tsx instead
  const isLoading = false; // Mock data loads instantly from App.tsx

  const [showRecordingsList, setShowRecordingsList] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [editingRecording, setEditingRecording] = useState(false);
  const [transcriptionText, setTranscriptionText] = useState('');
  const recordingTimer = useRef<NodeJS.Timeout>();
  const [recordingDuration, setRecordingDuration] = useState(0);

  // Update transcription when recording
  useEffect(() => {
    if (isRecordingVisit && currentDictation?.transcription) {
      const fullTranscription = currentDictation.transcription
        .map(segment => segment.text)
        .join(' ');
      updateVisitRecordingTranscription(fullTranscription);
    }
  }, [currentDictation, isRecordingVisit, updateVisitRecordingTranscription]);

  // Recording duration timer
  useEffect(() => {
    if (isRecordingVisit) {
      const startTime = Date.now();
      recordingTimer.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setRecordingDuration(elapsed);
        if (activeRecording) {
          updateActiveRecording({ duration: elapsed });
        }
      }, 1000);
    } else {
      if (recordingTimer.current) {
        clearInterval(recordingTimer.current);
      }
      setRecordingDuration(0);
    }
    
    return () => {
      if (recordingTimer.current) {
        clearInterval(recordingTimer.current);
      }
    };
  }, [isRecordingVisit, activeRecording, updateActiveRecording]);

  const handleStartRecording = async () => {
    const patient = patients.find(p => p.id === selectedPatientId);
    startVisitRecording(
      selectedPatientId, 
      patient ? getLocalizedValue(patient.name, language) : undefined
    );
    await startDictation(selectedPatientId);
  };

  const handleStopRecording = async () => {
    await stopDictation();
    stopVisitRecording();
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSaveEdit = () => {
    if (activeRecording) {
      updateActiveRecording({ transcription: transcriptionText });
      setEditingRecording(false);
    }
  };

  const handleAIRewrite = (instruction: string) => {
    // Add context for AI to rewrite
    if (activeRecording) {
      addContext({
        id: `rewrite-${Date.now()}`,
        type: 'action',
        data: {
          action: 'rewrite',
          instruction,
          content: activeRecording.transcription
        },
        label: `Rewrite: ${instruction}`
      });
    }
  };

  const aiRewriteOptions = [
    { 
      icon: <FileText size={16} />, 
      label: language === 'en' ? 'SOAP Format' : 'تنسيق SOAP',
      instruction: 'Rewrite this visit recording in SOAP format (Subjective, Objective, Assessment, Plan)'
    },
    {
      icon: <Sparkles size={16} />,
      label: language === 'en' ? 'Clinical Summary' : 'ملخص سريري',
      instruction: 'Create a concise clinical summary with key findings and recommendations'
    },
    {
      icon: <CheckCircle size={16} />,
      label: language === 'en' ? 'Extract Action Items' : 'استخراج بنود العمل',
      instruction: 'Extract all action items, medications, tests ordered, and follow-up instructions'
    }
  ];

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2" 
            style={{ color: 'var(--color-text-primary)' }}>
          <Mic size={20} />
          {language === 'en' ? 'Visit Recording' : 'تسجيل الزيارة'}
        </h3>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setShowRecordingsList(!showRecordingsList)}
          className="relative"
        >
          <FileText />
          {visitRecordings.length > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
              {visitRecordings.length}
            </span>
          )}
        </Button>
      </div>

      {/* Recording Controls */}
      {!activeRecording && !isRecordingVisit && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-2" 
                   style={{ color: 'var(--color-text-secondary)' }}>
              {language === 'en' ? 'Select Patient' : 'اختر المريض'}
            </label>
            <BrandSelect
              value={selectedPatientId || null}
              onChange={(v) => setSelectedPatientId(v ?? '')}
              className="w-full"
              placeholder={
                isLoading
                  ? (language === 'en' ? 'Loading patients...' : 'جاري تحميل المرضى...')
                  : !isLoading && patients.length === 0
                    ? (language === 'en' ? 'No patients available' : 'لا يوجد مرضى')
                    : (language === 'en' ? 'Select a patient...' : 'اختر مريض...')
              }
              disabled={isLoading || patients.length === 0}
              options={patients.map(patient => ({
                value: patient.id,
                label: getLocalizedValue(patient.name, language),
              }))}
            />
          </div>

          <Button
            onClick={handleStartRecording}
            disabled={!selectedPatientId}
            block
            size="lg"
          >
            <Mic />
            {language === 'en' ? 'Start Recording Visit' : 'بدء تسجيل الزيارة'}
          </Button>
        </div>
      )}

      {/* Active Recording */}
      {isRecordingVisit && activeRecording && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border" style={{ 
            borderColor: 'var(--color-error)',
            backgroundColor: 'rgba(220, 38, 38, 0.05)'
          }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <CircularVisualizer
                  isActive={isRecording}
                  level={audioLevel}
                  size={40}
                />
                <div>
                  <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    {language === 'en' ? 'Recording...' : 'جاري التسجيل...'}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    <Clock size={14} className="inline me-1" />
                    {formatDuration(recordingDuration)}
                  </p>
                </div>
              </div>
              <Button
                variant="destructive"
                size="icon"
                onClick={handleStopRecording}
                className="rounded-full"
                aria-label="Stop recording"
              >
                <MicOff />
              </Button>
            </div>

            {activeRecording.patientName && (
              <p className="text-sm flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                <User size={14} />
                {activeRecording.patientName}
              </p>
            )}
          </div>

          {/* Live Transcription */}
          {activeRecording.transcription && (
            <div className="p-3 rounded-lg border" style={{
              borderColor: 'var(--color-border-primary)',
              backgroundColor: 'var(--color-bg-secondary)'
            }}>
              <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                {activeRecording.transcription}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Saved Recording View */}
      {activeRecording && !isRecordingVisit && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border" style={{
            borderColor: 'var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-secondary)'
          }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-medium flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                  {activeRecording.patientName || language === 'en' ? 'Unknown Patient' : 'مريض غير معروف'}
                </h4>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  <Calendar size={14} className="inline me-1" />
                  {formatDateTime(activeRecording.date)}
                  <span className="mx-2">•</span>
                  <Clock size={14} className="inline me-1" />
                  {formatDuration(activeRecording.duration)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setEditingRecording(!editingRecording)}
                aria-label="Edit recording"
              >
                <Edit3 />
              </Button>
            </div>

            {/* Transcription */}
            {editingRecording ? (
              <div className="space-y-3">
                <Textarea
                  value={transcriptionText}
                  onChange={(e) => setTranscriptionText(e.target.value)}
                  rows={8}
                />
                <div className="flex gap-2">
                  <Button
                    variant="success"
                    onClick={handleSaveEdit}
                  >
                    <Save />
                    {language === 'en' ? 'Save' : 'حفظ'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setEditingRecording(false)}
                  >
                    {language === 'en' ? 'Cancel' : 'إلغاء'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="prose max-w-none">
                <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--color-text-primary)' }}>
                  {activeRecording.transcription || (
                    <span className="italic" style={{ color: 'var(--color-text-tertiary)' }}>
                      {language === 'en' ? 'No transcription available' : 'لا يوجد نص متاح'}
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* AI Rewrite Options */}
            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--color-border-primary)' }}>
              <p className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                {language === 'en' ? 'AI Actions' : 'إجراءات الذكاء الاصطناعي'}
              </p>
              <div className="grid grid-cols-1 gap-2">
                {aiRewriteOptions.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAIRewrite(option.instruction)}
                    className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors text-start"
                    style={{ borderColor: 'var(--color-border-primary)' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span className="text-blue-600">{option.icon}</span>
                    <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recordings List */}
      {showRecordingsList && visitRecordings.length > 0 && (
        <div className="absolute top-12 right-0 w-80 max-h-96 overflow-y-auto rounded-lg shadow-xl border-2 z-50"
             style={{
               backgroundColor: 'var(--color-bg-primary)',
               borderColor: 'var(--color-primary-600)',
               boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
             }}>
          <div className="p-3 border-b" style={{ 
            borderColor: 'var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-secondary)'
          }}>
            <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {language === 'en' ? 'Previous Recordings' : 'التسجيلات السابقة'}
            </h4>
          </div>
          <div className="p-2">
            {visitRecordings.map((recording) => (
              <div
                key={recording.id}
                className="p-3 rounded-lg cursor-pointer transition-all border mb-2"
                style={{ 
                  borderColor: 'var(--color-border-primary)',
                  backgroundColor: 'var(--color-bg-secondary)',
                  // Force text color inheritance
                  color: 'var(--color-text-primary)'
                }}
                onClick={() => {
                  loadVisitRecording(recording.id);
                  setShowRecordingsList(false);
                  setTranscriptionText(recording.transcription);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                  e.currentTarget.style.borderColor = 'var(--color-primary-600)';
                  e.currentTarget.style.transform = 'translateX(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  e.currentTarget.style.borderColor = 'var(--color-border-primary)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-sm" style={{ 
                      color: 'var(--color-text-primary)',
                      opacity: 1
                    }}>
                      {recording.patientName || 'Unknown Patient'}
                    </p>
                    <p className="text-xs mt-1" style={{ 
                      color: 'var(--color-text-secondary)',
                      opacity: 1
                    }}>
                      {formatDate(recording.date)} • {formatDuration(recording.duration)}
                    </p>
                    {recording.summary && (
                      <p className="text-xs mt-2 line-clamp-2" style={{ 
                        color: 'var(--color-text-tertiary)',
                        opacity: 1
                      }}>
                        {recording.summary}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteVisitRecording(recording.id);
                    }}
                    className="ms-2 hover:text-[var(--color-error)]"
                    aria-label="Delete recording"
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};